import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMail, FiLock, FiUser, FiArrowRight, FiBriefcase } = FiIcons;

const Signup = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (step < 2) {
      setStep(step + 1);
    } else {
      navigate('/onboarding');
    }
  };

  return (
    <div className="min-h-screen bg-brand-ivory flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-xl">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block text-3xl font-display font-bold text-brand-indigo hover:opacity-80 transition-opacity">
            hd.ai
          </Link>
          <h1 className="text-3xl font-display font-bold text-brand-indigo mt-6">Join HDAI</h1>
          <p className="text-gray-500 mt-2">Start building clinical-grade AI today.</p>
        </div>

        <div className="bg-white rounded-[32px] shadow-2xl p-10 border border-gray-100">
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Step {step} of 2</span>
              <span className="text-xs font-bold text-brand-indigo">{step === 1 ? 'Account Details' : 'Clinical Role'}</span>
            </div>
            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-brand-mint transition-all duration-300 ease-out" 
                style={{ width: `${(step / 2) * 100}%` }}
              ></div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {step === 1 ? (
              <div className="space-y-5 animate-in fade-in slide-in-from-right-4 duration-500">
                <div>
                  <label className="block text-sm font-bold text-brand-indigo mb-2">Full Name</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <SafeIcon icon={FiUser} className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      required
                      className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-mint focus:border-transparent transition-all outline-none"
                      placeholder="Dr. Sarah Jenkins"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-brand-indigo mb-2">Work Email</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <SafeIcon icon={FiMail} className="text-gray-400" />
                    </div>
                    <input
                      type="email"
                      required
                      className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-mint focus:border-transparent transition-all outline-none"
                      placeholder="sarah@hospital.org"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-brand-indigo mb-2">Password</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <SafeIcon icon={FiLock} className="text-gray-400" />
                    </div>
                    <input
                      type="password"
                      required
                      className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-mint focus:border-transparent transition-all outline-none"
                      placeholder="••••••••"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-5 animate-in fade-in slide-in-from-right-4 duration-500">
                <div>
                  <label className="block text-sm font-bold text-brand-indigo mb-2">Primary Role</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <SafeIcon icon={FiBriefcase} className="text-gray-400" />
                    </div>
                    <select
                      required
                      className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-mint focus:border-transparent transition-all outline-none appearance-none"
                    >
                      <option value="" disabled selected>Select your role</option>
                      <option value="physician">Physician / Clinician</option>
                      <option value="developer">Developer / Engineer</option>
                      <option value="informatics">Clinical Informatics</option>
                      <option value="founder">Health Tech Founder</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-brand-indigo mb-2">Organization Name</label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-mint focus:border-transparent transition-all outline-none"
                    placeholder="e.g. Memorial Health System"
                  />
                </div>
              </div>
            )}

            <button
              type="submit"
              className="w-full py-4 bg-brand-indigo text-white rounded-xl font-bold flex items-center justify-center space-x-2 hover:bg-brand-slate transition-colors shadow-lg active:scale-[0.98]"
            >
              <span>{step === 1 ? 'Continue' : 'Complete Setup'}</span>
              <SafeIcon icon={FiArrowRight} />
            </button>
          </form>
        </div>
        
        <p className="text-center mt-8 text-xs text-gray-400">
          By continuing, you agree to our <Link to="/legal" className="underline hover:text-brand-indigo">Terms of Service</Link> and <Link to="/legal" className="underline hover:text-brand-indigo">HIPAA BAA</Link>.
        </p>
      </div>
    </div>
  );
};

export default Signup;